import type { Thread, ThreadSystem, Handedness } from '@frontend/api';
import { RUNES_CALLOUT_TYPES } from '@frontend/api';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
} from '@frontend/legacy-components';

import { ThreadSystemSelect, HandednessSelect } from './TypedSelects';

export type ThreadFieldValues = {
  threadSystem: ThreadSystem;
  size: string;
  pitch: string;
  threadDepth: string;
  toleranceClass: string;
  handedness?: Handedness;
};

type ThreadFieldGroupProps = {
  values: ThreadFieldValues;
  onChange: <K extends keyof ThreadFieldValues>(
    field: K,
    value: ThreadFieldValues[K],
  ) => void;
  errors?: Partial<Record<keyof ThreadFieldValues, string>>;
  touched?: Partial<Record<keyof ThreadFieldValues, boolean>>;
};

/**
 * Builds a raw thread string summary from thread field values.
 * Format: "{size} {SYSTEM}-{pitch} {toleranceClass} {handedness} depth {threadDepth}"
 */
export const buildRawThreadStr = (values: ThreadFieldValues): string => {
  let raw = `${values.size} ${values.threadSystem.toUpperCase()}`;
  if (values.pitch) {
    raw += `-${values.pitch}`;
  }
  if (values.toleranceClass) {
    raw += ` ${values.toleranceClass}`;
  }
  if (values.handedness) {
    raw += ` ${values.handedness}`;
  }
  if (values.threadDepth) {
    raw += ` depth ${values.threadDepth}`;
  }
  return raw;
};

/**
 * Builds a Thread callout object and its raw string representation from form values.
 */
export const buildThreadObject = (
  values: ThreadFieldValues,
): { thread: Thread; rawThreadStr: string } => {
  const rawThreadStr = buildRawThreadStr(values);
  const thread: Thread = {
    calloutType: RUNES_CALLOUT_TYPES.THREAD,
    threadSystem: values.threadSystem,
    size: values.size,
    pitch: values.pitch || undefined,
    threadDepth: values.threadDepth || undefined,
    toleranceClass: values.toleranceClass || undefined,
    handedness: values.handedness,
    rawThreadStr,
  };
  return { thread, rawThreadStr };
};

/**
 * Shared field group for thread specification, used by both the standalone
 * ThreadForm and the thread sub-section within HoleForm.
 */
export const ThreadFieldGroup = ({
  values,
  onChange,
  errors,
  touched,
}: ThreadFieldGroupProps) => (
  <>
    <ThreadSystemSelect
      name="threadSystem"
      label="Thread System"
      value={values.threadSystem}
      onChange={(value) => onChange('threadSystem', value)}
      error={errors?.threadSystem}
      touched={touched?.threadSystem}
    />

    <FormControl>
      <FormLabel>Size</FormLabel>
      <Input
        name="size"
        value={values.size}
        onChange={(e) => onChange('size', e.target.value)}
        size="sm"
        placeholder="e.g., #1, 1/4, M8"
      />
      {errors?.size && touched?.size && (
        <FormErrorMessage>{errors.size}</FormErrorMessage>
      )}
    </FormControl>

    <FormControl>
      <FormLabel>Pitch (optional)</FormLabel>
      <Input
        name="pitch"
        value={values.pitch}
        onChange={(e) => onChange('pitch', e.target.value)}
        size="sm"
        placeholder="e.g., 40, 1.75"
      />
      {errors?.pitch && touched?.pitch && (
        <FormErrorMessage>{errors.pitch}</FormErrorMessage>
      )}
    </FormControl>

    <FormControl>
      <FormLabel>Thread Depth (optional)</FormLabel>
      <Input
        name="threadDepth"
        value={values.threadDepth}
        onChange={(e) => onChange('threadDepth', e.target.value)}
        size="sm"
        placeholder="e.g., 0.5, 0.75"
      />
    </FormControl>

    <FormControl>
      <FormLabel>Tolerance Class (optional)</FormLabel>
      <Input
        name="toleranceClass"
        value={values.toleranceClass}
        onChange={(e) => onChange('toleranceClass', e.target.value)}
        size="sm"
        placeholder="e.g., 1A, 6H, 4g"
      />
    </FormControl>

    <HandednessSelect
      name="handedness"
      label="Handedness (optional)"
      value={values.handedness}
      onChange={(value) => onChange('handedness', value)}
      error={errors?.handedness}
      touched={touched?.handedness}
    />
  </>
);
